import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { CardHeader } from "../components/CardHeader";
import { formatHeadline } from "../cardUtils";
import { timeframeToString } from "src/utils/date";
import { limitWords } from "src/utils/strings";
import { Markdown } from "react-markdown/lib";
import remarkGfm from "remark-gfm";

interface CardHeaderData {
  headline: string;
  description: string;
  evidence: string[];
}

// CardHeader Block Recipe
const cardHeaderRecipe = {
  id: "card-header-block",
  name: "Card Header Block",
  globalKey: [],
  prefix: `{
            "headline": "string - Write a bold, specific headline. If a contributor or theme exceeds 33% of activity, you may highlight their leadership. Otherwise, emphasize collaboration, group effort, or distribution.",
            "description": "string — expand on major shifts, workstreams, and context. Use bold for names, topics, or notable numbers.",
            "evidence": [
              "Include metric shifts, themes, key PRs or issues. Always support narrative claims with actual data."
            ]
    }`,
  //TODO change this to CardHeader Block
  format: {
    type: "json",
    fields: ["headline", "description", "evidence"],
    constraints: [{ field: "headline", type: "json", required: true }],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as CardHeaderData;
          return Boolean(parsed.headline);
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// CardHeader Block Parser
function cardHeaderParser(data: any) {
  try {
    //TODO run validatino and error handling here
    return {
      data: data[cardHeaderBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse card header data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// CardHeader Block Variants
const cardHeaderVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard header with title and optional description",
    defaultSettings: {
      showDescription: true,
      showTag: true,
      showTimeframe: true,
    },
  },
  {
    id: "feed-card",
    name: "Feed Card",
    description:
      "Standard header with title and optional description for the feed ",
    defaultSettings: {
      showDescription: true,
      showTag: true,
      showTimeframe: true,
    },
  },
  {
    id: "compact",
    name: "Compact",
    description: "Minimal header with just title",
    defaultSettings: {
      showDescription: false,
      showTag: false,
      showTimeframe: false,
    },
  },
  {
    id: "title-only",
    name: "Title Only",
    description: "Minimal header with just title",
    defaultSettings: {
      showDescription: false,
      showTag: true,
      showTimeframe: true,
    },
  },
  {
    id: "bold",
    name: "Bold",
    description: "Prominent header with emphasis",
    defaultSettings: {
      showDescription: true,
      showTag: true,
      showTimeframe: true,
      style: {
        padding: "var(--padding-3)",
        lineHeight: "1.2",
      },
    },
  },
];

// CardHeader Block Renderer
function CardHeaderBlockRenderer(props: any) {
  const {
    data,
    variant,
    settings,
    className,
    style,
    processedRecords,
    totalRecords,
    cta,
  } = props;
  if (data === undefined) {
    return null;
  }

  const variantSettings = cardHeaderVariants.find(
    (v) => v.id === variant
  )?.defaultSettings;
  return (
    <div className="vzbl-panel-container">
      <CardHeader
        type="summary"
        {...data}
        variant={variant}
        processedRecords={processedRecords}
        totalRecords={totalRecords}
        title={formatHeadline(data[cardHeaderBlock.id])}
        showDescription={variantSettings?.showDescription}
        showTag={variantSettings?.showTag}
        showTimeframe={variantSettings?.showTimeframe}
        description={data[cardHeaderBlock.id]?.description}
        tag={props.link?.name}
        timeframe={timeframeToString(props.link?.json?.defaultDateRange)}
        cta={cta}
        className={`${className}`}
        style={{
          ...style,
          ...settings?.style,
        }}
      />
    </div>
  );
}

// Create CardHeader Block
const cardHeaderBlock = createBaseBlock({
  id: "card-header",
  name: "Card Header Block",
  description: "Displays a card header with title and optional metadata",
  recipe: cardHeaderRecipe,
  variants: cardHeaderVariants,
  parser: cardHeaderParser,
  renderer: CardHeaderBlockRenderer,
});

// Export the block for direct use
export { cardHeaderBlock };
